import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { useEffect, useState } from "react";
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import Select from "react-select";

export default function EditUserMngt({ showEdit, closeModal, refreshPage, userDetails }) {
  const [validationError, setValidationError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [userType, setUserType] = useState([]);
  const [warehouse, setWarehouse] = useState([]);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);

  // Fetch roles, user types, and warehouses
  useEffect(() => {
    const fetchRolesAndPermissions = async () => {
      try {
        const userTypeRes = await axiosInstance.get(`/user/auth/get_user_type`);
        const rolesRes = await axiosInstance.get("/user/roles");
        const warehousesRes = await axiosInstance.get('/user/warehouses/get_warehouse');

        const filteredRoles = rolesRes.data.data.roles.filter(role => role.name !== "Customers");

        setUserType(userTypeRes.data.data.user_type || []);
        setRoles(filteredRoles);
        setWarehouse(warehousesRes.data.data.warehouse || []);
      } catch (error) {
        console.error("Error fetching roles, user types, or warehouses:", error);
        toast.error("Failed to load roles, user types, or warehouses.");
      }
    };

    fetchRolesAndPermissions();
  }, []);

  // Pre-fill form when userDetails changes
  useEffect(() => {
    if (!userDetails || warehouse.length === 0) return;

    setFormData({
      first_name: userDetails.first_name || "",
      last_name: userDetails.last_name || "",
      email: userDetails.email || "",
      phone: userDetails.phone || "",
    });

    setSelectedRole(userDetails.roles[0]?.id || "");
    setSelectedUser(userDetails.user_type_id || "");

    // Pre-fill warehouses
    if (userDetails.ware_house_id && userDetails.ware_house_id.length > 0) {
      // For Sales Supervisor (userType 3, role 3) - multi-select
      if (userDetails.user_type_id == 3 && userDetails.roles[0]?.id == 3) {
        const selected = userDetails.ware_house_id
          .map(id => {
            const wh = warehouse.find(w => w.id === id);
            return wh ? { value: wh.id, label: wh.name } : null;
          })
          .filter(Boolean);
        setSelectedWarehouses(selected);
      } 
      // For other roles - single select
      else {
        const firstWarehouseId = userDetails.ware_house_id[0];
        const wh = warehouse.find(w => w.id === firstWarehouseId);
        if (wh) {
          setSelectedWarehouses([{ value: wh.id, label: wh.name }]);
        } else {
          setSelectedWarehouses([]);
        }
      }
    } else {
      setSelectedWarehouses([]);
    }
  }, [userDetails, warehouse]);

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleClose = () => {
    closeModal();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        role: selectedRole,
        user_type_id: selectedUser,
        ware_house_id: selectedWarehouses.map(w => w.value), // send JSON array
      };

      const response = await axiosInstance.put(`/user/editUser/${userDetails.id}`, payload);

      if (response.data.success) {
        toast.success("User updated successfully!");
        refreshPage();
        handleClose();
      } else {
        console.error("Update failed:", response.data.message);
        toast.error("Failed to update user.");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update user.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={showEdit} onHide={handleClose} dialogClassName="modal-lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User and Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              readOnly
              style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>User Type</Form.Label>
            <Form.Select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
            >
              <option value="">-- Select User Type --</option>
              {userType.map(user => (
                <option key={user.id} value={user.id}>{user.user_type_name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={selectedRole}
              onChange={e => setSelectedRole(e.target.value)}
            >
              <option value="">-- Select Role --</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Warehouse selection - Multi-select for Sales Supervisor (userType 3, role 3) */}
          {(selectedUser == 3 && selectedRole == 3) && (
            <Form.Group className="mb-3">
              <Form.Label>Assign Warehouses</Form.Label>
              <Select
                isMulti
                options={warehouse.map(w => ({ value: w.id, label: w.name }))}
                value={selectedWarehouses}
                onChange={setSelectedWarehouses}
                placeholder="Select warehouses..."
              />
            </Form.Group>
          )}

          {/* Warehouse selection - Single select for other roles that require warehouse assignment */}
          {((selectedUser == 8 && selectedRole == 8) ||
            (selectedUser == 10 && selectedRole == 10) ||
            (selectedUser == 5 && selectedRole == 6)) && (
              <Form.Group className="mb-3">
                <Form.Label>Warehouse</Form.Label>
                <Form.Select
                  value={selectedWarehouses[0]?.value || ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue) {
                      const selectedWarehouse = warehouse.find(w => w.id == selectedValue);
                      if (selectedWarehouse) {
                        setSelectedWarehouses([{ value: selectedWarehouse.id, label: selectedWarehouse.name }]);
                      }
                    } else {
                      setSelectedWarehouses([]);
                    }
                  }}
                >
                  <option value="">-- Select Warehouse --</option>
                  {warehouse.map((ware) => (
                    <option key={ware.id} value={ware.id}>
                      {ware.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Close</Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
